// -----------------------------------------------------------------------------
// safeDisplay.js — defensive formatters for audit-adjacent surfaces.
//
// Root cause of the "Objects are not valid as a React child" crash:
// some backend audit / proof-ledger writers passed a Python dict (e.g.
// {"internal_brief_id": "...", "sync_brief_id": "..."}) into the `notes`
// field. Historically the field was a plain string, so downstream React
// components rendered it directly as `{a.notes}` and crashed at runtime.
//
// This helper NEVER renders raw JSON, `[object Object]`, or an object payload
// in the interface. If it encounters an unknown / malformed object with none
// of the approved display keys, it returns the caller's chosen fallback
// (default: em dash "—"). Approved display keys are, in priority order:
//   internal_brief_id → sync_brief_id → brief_id → id → ref
//
// The helpers are pure and side-effect-free with one exception: unexpected
// object shapes trigger a single dev-only console.warn (guarded behind
// NODE_ENV !== "production") to help future debugging. No PII / secrets
// are emitted — only the object keys are logged.
// -----------------------------------------------------------------------------

const APPROVED_REF_KEYS = [
  "internal_brief_id",
  "sync_brief_id",
  "brief_id",
  "id",
  "ref",
];

const EM_DASH = "\u2014";
const IS_DEV = typeof process !== "undefined"
  && process.env
  && process.env.NODE_ENV !== "production";

let _warnCount = 0;
function warnOnce(shape) {
  if (!IS_DEV) return;
  if (_warnCount > 25) return; // guard against noisy loops
  _warnCount += 1;
  // Log keys only — never the value payload.
  try {
    console.warn("[safeDisplay] Rendered unknown object shape; falling back safely.", { keys: Object.keys(shape || {}) });
  } catch (_e) { /* no-op */ }
}

/**
 * Return `true` if the value is a plain object (not null, not array, not Date).
 */
function isPlainObject(val) {
  return (
    val !== null
    && typeof val === "object"
    && !Array.isArray(val)
    && !(val instanceof Date)
  );
}

/**
 * Extract a human-readable reference id from an object using the approved
 * priority order. Returns `null` if none of the approved keys are present
 * (or all present values are empty).
 */
export function pickBriefRef(val) {
  if (!isPlainObject(val)) return null;
  for (const key of APPROVED_REF_KEYS) {
    const v = val[key];
    if (v == null) continue;
    if (typeof v === "string") {
      const trimmed = v.trim();
      if (trimmed) return trimmed;
    } else if (typeof v === "number" && Number.isFinite(v)) {
      return String(v);
    }
  }
  return null;
}

/**
 * Safely convert any value that might reach a JSX child into a display string.
 *
 * Handles:
 *  - null / undefined / "" → fallback
 *  - string / finite number → returned verbatim (numbers as strings)
 *  - boolean → "Yes" / "No"
 *  - Date → ISO string
 *  - Array → items formatted individually via `safeText` and joined with `", "`.
 *            An empty array returns the fallback.
 *  - Plain object → prefer `internal_brief_id` → `sync_brief_id` → `brief_id`
 *                   → `id` → `ref`, else fallback. Never JSON-stringified.
 *  - Anything else (functions, symbols, etc.) → fallback
 *
 * @param {*} val
 * @param {{ fallback?: string, joinWith?: string }} [opts]
 * @returns {string}
 */
export function safeText(val, opts = {}) {
  const fallback = Object.prototype.hasOwnProperty.call(opts, "fallback")
    ? opts.fallback
    : EM_DASH;
  const joinWith = opts.joinWith || ", ";

  if (val == null) return fallback;
  if (typeof val === "string") {
    return val.length ? val : fallback;
  }
  if (typeof val === "number") {
    return Number.isFinite(val) ? String(val) : fallback;
  }
  if (typeof val === "boolean") {
    return val ? "Yes" : "No";
  }
  if (val instanceof Date) {
    try { return val.toISOString(); } catch (_e) { return fallback; }
  }
  if (Array.isArray(val)) {
    if (val.length === 0) return fallback;
    const parts = val
      .map((item) => safeText(item, { fallback: "", joinWith }))
      .filter((s) => s && s.length);
    return parts.length ? parts.join(joinWith) : fallback;
  }
  if (isPlainObject(val)) {
    const ref = pickBriefRef(val);
    if (ref) return ref;
    warnOnce(val);
    return fallback;
  }
  // functions, symbols, bigints, etc.
  return fallback;
}

/**
 * Format the `notes` field on an audit / proof-ledger / document record.
 * This is the primary entry point used by AuditLog, ProofLedger, TrackEditor,
 * RestrictedIdentityDesk, VaultDocketDesk, and any related audit surface.
 *
 * Defaults to an empty string when the notes field is missing so that
 * downstream expressions like `notes || ""` and truthy-guards keep working.
 *
 * @param {*} val
 * @param {{ fallback?: string }} [opts]  Override the empty/malformed fallback.
 * @returns {string}
 */
export function formatNotes(val, opts = {}) {
  const fallback = Object.prototype.hasOwnProperty.call(opts, "fallback")
    ? opts.fallback
    : "";
  return safeText(val, { fallback });
}

/**
 * Convenience truthy check — safe replacement for `if (rec.notes)` guards.
 * Returns `true` only if `formatNotes(val)` produces a visible string.
 */
export function hasVisibleNotes(val) {
  const s = formatNotes(val, { fallback: "" });
  return typeof s === "string" && s.trim().length > 0;
}

/**
 * Format a value used inside before/after diff cells in the Proof Ledger.
 * Prefers a readable brief id; falls back to "Not assigned" when a
 * malformed object is encountered so a diff row still renders.
 */
export function formatDiffValue(val) {
  return safeText(val, { fallback: "Not assigned" });
}

export const _internal = {
  APPROVED_REF_KEYS,
  EM_DASH,
  isPlainObject,
};
